import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";

import { VehicleDataDto } from "../../Pages/Vehicles/interfaces";
import {
  ErrorEntry,
  INPUT_LENGTH,
  INPUT_VALUES_LENGTH,
  REASSIGN_DATA_VALUES,
  ReassignVehicleData,
  VehicleErrorMessages
} from "..";
import useImeiValidation from "./useImeiValidation";

const useVehiclesFormInputs = (rowData?: VehicleDataDto) => {
  const { t } = useTranslation();

  const [vehicleName, setVehicleName] = useState<string>(rowData?.number ?? "");
  const [imeiValue, setImeiValue] = useState<string>(rowData?.imei ?? "");
  const [descriptionValue, setDescriptionValue] = useState<string>(
    rowData?.comment ?? ""
  );

  const [isVehicleDeactivated, setIsVehicleDeactivated] = useState<boolean>(
    rowData?.disabled ?? false
  );
  const [nameInputError, setNameInputError] = useState<string | null>(null);
  const [descriptionInputError, setDescriptionInputError] = useState<
    string | null
  >(null);

  const { isValidOptionalImei, imeiError, setImeiError, validateImei } =
    useImeiValidation(imeiValue);

  useEffect(() => {
    if (rowData) {
      //validate fields logic here
      validateNameLength(rowData.number ?? "");
    }
  }, []);

  const validateNameLength = (
    input: string | ChangeEvent<HTMLInputElement>
  ) => {
    const value = typeof input === "string" ? input : input.target.value;

    if (value.length === 0) {
      setNameInputError(t("Dialog##vrn should not be empty"));
      return;
    }

    if (value.length > INPUT_LENGTH.VehicleName) {
      setNameInputError(
        t("Dialog##field length not valid", {
          maxNumber: INPUT_LENGTH.VehicleName
        })
      );
    }
  };

  const validateDescriptionLength = (e: any) => {
    const inputValue = e.target.value;
    if (inputValue.length > INPUT_LENGTH.Description) {
      setDescriptionInputError(
        t("Dialog##description not valid", {
          maxNumber: INPUT_LENGTH.Description
        })
      );
    }
  };

  const resetAllValues = () => {
    setVehicleName("");
    setImeiValue("");
		setDescriptionValue("");
    setImeiError?.(null);
    setNameInputError(null);
    setDescriptionInputError(null);
    setIsVehicleDeactivated(false);
  };

  const handleChangeNameValue = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setVehicleName(e.target.value);
    validateNameLength(e);
    setNameInputError(null);
  };

  const handleChangeImeiValue = (e: ChangeEvent<HTMLInputElement>) => {
    const imeiValueFromInput = e.target.value;

    e.preventDefault();
    setImeiValue(imeiValueFromInput);
    validateImei(e);

    imeiValueFromInput.length <= INPUT_VALUES_LENGTH.Imei &&
      setImeiError?.(null);
  };

  const handleChangeDescription = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDescriptionValue(e.target.value);
    validateDescriptionLength(e);
    setDescriptionInputError(null);
  };

  const handleToggleDeactivateVehicle = () => {
    setIsVehicleDeactivated(!isVehicleDeactivated);
  };

  const shouldVehicleNameValidate =
    vehicleName &&
    vehicleName.length <= INPUT_LENGTH.VehicleName &&
    !nameInputError;

  const handleCustomError = (
    error: unknown,
    setReassignData: Dispatch<SetStateAction<ReassignVehicleData | null>>,
    setAccessibleDialogOpen: Dispatch<SetStateAction<boolean>>,
    setInaccessibleImeiOpen: Dispatch<SetStateAction<boolean>>
  ) => {
    const err = error as AxiosError<any>;
    const { message, entries = [] } = err.response?.data ?? {};

    if (message === VehicleErrorMessages.ImeiAlreadyAssignedAccessible) {
      const findEntry = (field: string) =>
        (entries as ErrorEntry[]).find(e => e.field === field)?.message;

      setReassignData({
        imei: findEntry(REASSIGN_DATA_VALUES.Imei),
        vehicleNumber: findEntry(REASSIGN_DATA_VALUES.VehicleNumber),
        company: findEntry(REASSIGN_DATA_VALUES.CompanyName)
      });

      setAccessibleDialogOpen(true);
      return;
    }

    if (message === VehicleErrorMessages.ImeiAlreadyAssignedInaccessible) {
      return setInaccessibleImeiOpen(true);
    }
  };

  return {
    descriptionInputError,
    descriptionValue,
    imeiError,
    imeiIsValid: isValidOptionalImei,
    imeiValue,
    isVehicleDeactivated,
    nameInputError,
    vehicleName,
    shouldVehicleNameValidate,
    handleChangeDescription,
    handleChangeImeiValue,
    handleChangeNameValue,
    handleToggleDeactivateVehicle,
    resetAllValues,
    setImeiError,
    setNameInputError,
    validateDescriptionLength,
    validateImei,
    validateNameLength,
    handleCustomError
  };
};

export default useVehiclesFormInputs;
